/**
 * The locations list — reads the existing narrow `GET /api/admin/locations` (open to every staff
 * role) rather than the wide detail route this feature adds, matching the same split the API keeps:
 * a list an agente can see, and a detail an agente cannot open. Both roles that reach this screen
 * can create a location; only `admin` can delete one, matching `admin-routes.ts`'s own bar for the
 * hard delete — the same asymmetry the Content Console has between its archive and delete actions.
 */
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  EmptyState,
  Skeleton,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  buttonVariants,
  cn,
  icons,
  toast,
} from '@apuestapr/ui';
import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import type { ApiFailure } from '../api/client.ts';
import { useSessionContext } from '../auth/SessionProvider.tsx';
import { useManagedLocationsList, type ManagedLocationsList } from './hooks.ts';
import { LocationStatusBadge } from './LocationStatusBadge.tsx';
import { locationPath, newLocationRoutePath } from './paths.ts';

export const LocationsFailureState = ({
  failure,
}: {
  readonly failure: ApiFailure;
}): ReactElement => {
  if (failure.kind === 'http' && failure.status === 403) {
    return <p role="alert">No tienes permiso para ver las localidades.</p>;
  }
  if (failure.kind === 'malformed') {
    return <p role="alert">La API respondió con datos que no coinciden con el contrato.</p>;
  }
  return <p role="alert">No se pudieron cargar las localidades.</p>;
};

interface DeleteLocationDialogProps {
  readonly label: string;
  readonly loading: boolean;
  readonly onDelete: () => Promise<boolean>;
}

/** Mirrors `content/ContentListPage.tsx`'s `DeleteContentDialog` — same confirm-then-destroy shape. */
const DeleteLocationDialog = ({
  label,
  loading,
  onDelete,
}: DeleteLocationDialogProps): ReactElement => {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="destructive">
          Eliminar <span className="sr-only">{label}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Eliminar permanentemente “{label}”</DialogTitle>
          <DialogDescription>
            “{label}” se eliminará permanentemente. Esta acción no se puede deshacer, y falla si la
            localidad todavía tiene retiros de caja, inventario, usuarios OTC, o llaves YubiKey
            asignadas.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button disabled={loading} variant="outline">
              Cancelar
            </Button>
          </DialogClose>
          <Button
            loading={loading}
            loadingLabel={`Eliminando ${label}…`}
            onClick={() => {
              void onDelete().then((deleted) => {
                if (deleted) setOpen(false);
              });
            }}
            variant="destructive"
          >
            Eliminar permanentemente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const LocationsListSkeleton = (): ReactElement => (
  <div
    aria-hidden="true"
    className="overflow-hidden rounded-xl border border-border bg-card p-4 text-card-foreground"
  >
    <Skeleton className="mb-4 h-4 w-48" />
    <div className="grid gap-3">
      {[0, 1, 2, 3].map((row) => (
        <div key={row} className="flex items-center gap-4">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-32" />
        </div>
      ))}
    </div>
  </div>
);

interface LocationsListViewProps {
  readonly canDelete: boolean;
  readonly list: ManagedLocationsList;
}

const LocationsListView = ({ canDelete, list }: LocationsListViewProps): ReactElement => {
  const busy = list.mutation.status === 'loading';

  if (list.state.status === 'loading') {
    return (
      <>
        <p className="sr-only" role="status">
          Cargando localidades…
        </p>
        <LocationsListSkeleton />
      </>
    );
  }

  if (list.state.status === 'error') return <LocationsFailureState failure={list.state.failure} />;

  const rows = list.state.data;

  return (
    <>
      {rows.length === 0 ? (
        <EmptyState
          icon={icons.layers}
          title="No hay localidades todavía"
          description="Crea la primera localidad con el botón de arriba."
        />
      ) : (
        <div
          aria-label="Tabla de localidades"
          className="overflow-x-auto rounded-xl border border-border bg-card text-card-foreground"
          role="region"
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
        >
          <Table>
            <TableCaption>
              {String(rows.length)} {rows.length === 1 ? 'localidad' : 'localidades'}, en el orden
              provisto por el servidor.
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Ciudad</TableHead>
                <TableHead>Dirección</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} data-location-id={row.id}>
                  <TableHead className="min-w-48 text-foreground" scope="row">
                    {row.nombre}
                  </TableHead>
                  <TableCell>{row.ciudad}</TableCell>
                  <TableCell className="min-w-56">{row.direccion}</TableCell>
                  <TableCell>{row.telefono ?? 'Sin teléfono'}</TableCell>
                  <TableCell>
                    <LocationStatusBadge estado={row.estado} />
                  </TableCell>
                  <TableCell>
                    <div className="flex min-w-max flex-wrap gap-2">
                      <Link
                        className={cn(buttonVariants({ size: 'sm', variant: 'outline' }))}
                        to={locationPath(row.id)}
                      >
                        Ver <span className="sr-only">{row.nombre}</span>
                      </Link>
                      {canDelete ? (
                        <DeleteLocationDialog
                          label={row.nombre}
                          loading={busy}
                          onDelete={async () => {
                            const result = await list.remove(row.id);
                            if (result.ok) {
                              toast({ title: `${row.nombre} eliminada`, variant: 'destructive' });
                            }
                            return result.ok;
                          }}
                        />
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {list.mutation.status === 'error' && list.mutation.failure.kind !== 'http' ? (
        <p className="mt-4" role="alert">
          No se pudo eliminar la localidad.
        </p>
      ) : null}
      {list.mutation.status === 'error' &&
      list.mutation.failure.kind === 'http' &&
      list.mutation.failure.status === 409 ? (
        <p className="mt-4" role="alert">
          {list.mutation.failure.details?.[0]?.message ??
            'Esta localidad todavía tiene historial asociado.'}
        </p>
      ) : null}
    </>
  );
};

export const LocationListPage = (): ReactElement => {
  const session = useSessionContext();
  const authenticated = session.state.status === 'authenticated' ? session.state.session : null;
  const csrfToken = authenticated?.csrfToken ?? '';
  const list = useManagedLocationsList(session.client, csrfToken, session.handleFailure);
  if (authenticated === null) return <></>;
  const canDelete = authenticated.user.rol === 'admin';

  return (
    <section aria-labelledby="locations-list">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="m-0" id="locations-list">
            Localidades
          </h2>
          <p className="mb-0 text-muted-foreground">
            Terminales, usuarios OTC y datos operativos de cada localidad.
          </p>
        </div>
        <Link className={buttonVariants({ variant: 'primary' })} to={newLocationRoutePath}>
          Crear localidad
        </Link>
      </div>
      <LocationsListView canDelete={canDelete} list={list} />
    </section>
  );
};
